import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {StaticImageData} from 'next/image';
import DisplayImage from '@/components/Ui-components/display-image';
import {
    contentMaxWidth,
    colors,
    borderRadius,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeight,
    lineHeightRatio,
    spacing,
    contentWidth,
    typography
} from '@/styles/tokens';
import parse from 'html-react-parser';
import Eyebrow from '@/components/Common/Eyebrow';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface DevProcessStep {
    title: string;
    phase: string;
    description: string;
    checklistItems: string[];
    tags: string[];
}

export interface DevProcessProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    steps: DevProcessStep[];
    chevronDown: StaticImageData;
    chevronRight: StaticImageData;
    checkIcon: StaticImageData;
}

// ─── sx constants ────────────────────────────────────────────────────────────────

const sectionHeadingSx = {
    textAlign: 'center',
    mb: spacing[40]
} as const;

const headingTypoSx = {
    ...typography.sectionHeading,
    color: colors.text.primary,
    mb: spacing[24],
    letterSpacing: letterSpacing.snug
} as const;

const subheadingTypoSx = {
    ...typography.body,
    color: colors.text.primary,
    maxWidth: contentMaxWidth.standard,
    mx: 'auto'
} as const;

const gridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: `${contentWidth.xs} 1fr`},
    gap: spacing[32],
    alignItems: 'start'
} as const;

const rightPanelSx = {
    bgcolor: colors.background.surfaceAlt,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.xl,
    p: spacing[28],
    display: 'flex',
    flexDirection: 'column'
} as const;

const phaseLabelSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.semibold,
    color: colors.accent.amber,
    letterSpacing: letterSpacing.mid,
    textTransform: 'uppercase' as const,
    lineHeight: lineHeight[20],
    mb: spacing[12]
} as const;

const rightHeadingSx = {
    fontSize: fontSize[28],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[40],
    mb: spacing[16]
} as const;

const descriptionSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28],
    mb: spacing[32]
} as const;

const tagWrapSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing[12]
} as const;

const tagSx = {
    display: 'inline-block',
    px: 1.5,
    py: 0.5,
    borderRadius: borderRadius.full,
    border: `1px solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.tag,
    fontSize: fontSize[14],
    color: colors.primary.deep,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeightRatio.snug
} as const;

const deliverablesLabelSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    mb: spacing[12]
} as const;

const numberCircleWrapSx = {
    flexShrink: 0,
    width: spacing[56],
    pr: spacing[16]
} as const;

const stepTitleSx = {
    flex: '1 0 0',
    minWidth: 0,
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const chevronWrapSx = {
    flexShrink: 0,
    width: spacing[20],
    height: spacing[20],
    position: 'relative' as const,
    ml: spacing[8]
} as const;

const checklistWrapSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    mb: spacing[32]
} as const;

const checkIconWrapSx = {
    flexShrink: 0,
    width: spacing[20],
    height: spacing[20],
    position: 'relative' as const
} as const;

const checklistItemTextSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

// ─── Main component ──────────────────────────────────────────────────────────────

const DevProcess = ({eyebrow, heading, subheading, steps, chevronDown, chevronRight, checkIcon}: DevProcessProps) => {
    const [activeIdx, setActiveIdx] = useState(0);
    const activeStep = steps[activeIdx];

    return (
        <MUIContainer>
            <Box sx={sectionHeadingSx}>
                {eyebrow && <Eyebrow text={eyebrow} />}
                <Typography variant="h1" component="h2" className="font-headers" sx={headingTypoSx}>
                    {heading}
                </Typography>
                <Typography className="font-regular-text" sx={subheadingTypoSx}>
                    {subheading}
                </Typography>
            </Box>

            <Box sx={gridSx}>
                {/* Left: step navigation list */}
                <Box>
                    {steps.map((step, idx) => {
                        const isActive = activeIdx === idx;
                        const isLast = idx === steps.length - 1;
                        return (
                            <Box
                                key={step.title}
                                onClick={() => setActiveIdx(idx)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: spacing[16],
                                    pt: spacing[16],
                                    pb: spacing[16],
                                    cursor: 'pointer',
                                    bgcolor: isActive ? colors.background.surfaceAlt : 'transparent',
                                    borderRadius: isActive ? borderRadius.md : 0,
                                    borderBottom: isLast ? 'none' : `1px solid ${isActive ? 'transparent' : colors.neutral.borderLight}`
                                }}
                            >
                                {/* Number circle */}
                                <Box sx={numberCircleWrapSx}>
                                    <Box
                                        sx={{
                                            bgcolor: isActive ? colors.primary.deep : colors.background.subtle,
                                            borderRadius: borderRadius.full,
                                            width: spacing[40],
                                            height: spacing[40],
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: fontSize[16],
                                                fontWeight: fontWeight.semibold,
                                                color: isActive ? colors.text.inverse : colors.text.primary,
                                                lineHeight: lineHeight[24]
                                            }}
                                        >
                                            {idx + 1}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Step title */}
                                <Typography component="h3" className="font-headers" sx={stepTitleSx}>
                                    {step.title}
                                </Typography>

                                {/* Chevron icon */}
                                <Box sx={chevronWrapSx}>
                                    <DisplayImage
                                        src={isActive ? chevronDown : chevronRight}
                                        alt={isActive ? 'Collapse step' : 'Expand step'}
                                        fill
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* Right: content panel */}
                <Box sx={rightPanelSx}>
                    <Typography sx={phaseLabelSx}>{activeStep.phase}</Typography>
                    <Typography className="font-headers" sx={rightHeadingSx}>
                        {activeStep.title}
                    </Typography>
                    <Typography className="font-regular-text" sx={descriptionSx}>
                        {activeStep.description}
                    </Typography>

                    {/* Checklist */}
                    <Box sx={checklistWrapSx}>
                        {activeStep.checklistItems.map((item, i) => (
                            <Box
                                key={item}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: spacing[12],
                                    pt: i === 0 ? 0 : spacing[12],
                                    pb: i < activeStep.checklistItems.length - 1 ? spacing[16] : 0,
                                    borderBottom:
                                        i < activeStep.checklistItems.length - 1 ? `1px solid ${colors.neutral.borderLight}` : 'none'
                                }}
                            >
                                <Box sx={checkIconWrapSx}>
                                    <DisplayImage src={checkIcon} alt="Included" fill />
                                </Box>
                                <Typography className="font-regular-text" sx={checklistItemTextSx}>
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Deliverables */}
                    {activeStep.tags.length > 0 && (
                        <>
                            <Typography className="font-headers" sx={deliverablesLabelSx}>
                                Deliverables
                            </Typography>
                            <Box sx={tagWrapSx}>
                                {activeStep.tags.map((tag) => (
                                    <Box key={tag} component="span" sx={tagSx}>
                                        {parse(tag)}
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </MUIContainer>
    );
};

export default memo(DevProcess);
